/**
 * ============================================================================
 * CompleteRequalificationDialog — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: close an IN_PROGRESS requalification. The server requires a
 *          signed REQUAL qualification document id; we collect it here
 *          along with the closing signature bundle.
 *
 * A11Y: focus moves to the document-id field; Escape closes; submit is
 *  disabled while the mutation is in flight.
 * ============================================================================ */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import dialogStyles from '../AuthorWorkbench/NewDocumentForm.module.css';
import styles from './RequalificationDashboard.module.css';
import {
  useCompleteRequalMutation,
  type CompleteRequalInput,
} from './useCompleteRequalMutation';
import type { RequalScheduleRow } from './useRequalificationScheduleQuery';

export interface CompleteRequalificationDialogProps {
  row: RequalScheduleRow;
  onClose: () => void;
  onCompleted: () => void;
}

export function CompleteRequalificationDialog({
  row,
  onClose,
  onCompleted,
}: CompleteRequalificationDialogProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const descId = useId();
  const firstRef = useRef<HTMLInputElement | null>(null);

  const completeM = useCompleteRequalMutation();
  const [docId, setDocId] = useState('');
  const [sig, setSig] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    firstRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    setError(null);
    if (!docId.trim()) {
      setError(
        t('equipment.requalDashboard.completeDialog.errors.docRequired', {
          defaultValue:
            'A signed REQUAL qualification document id is required.',
        }),
      );
      return;
    }
    if (!sig.trim()) {
      setError(
        t('equipment.requalDashboard.completeDialog.errors.sigRequired', {
          defaultValue: 'A signature bundle id is required.',
        }),
      );
      return;
    }
    const input: CompleteRequalInput = {
      requalificationId: row.requalification_id,
      qualification_document_id: docId.trim(),
      signature_bundle_id: sig.trim(),
    };
    try {
      await completeM.mutateAsync(input);
      onCompleted();
    } catch (err: unknown) {
      setError(
        err instanceof Error
          ? err.message
          : t(
              'equipment.requalDashboard.completeDialog.errors.serverFailed',
              {
                defaultValue: 'Server rejected the completion.',
              },
            ),
      );
    }
  }

  return (
    <div
      className={dialogStyles.backdrop}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={dialogStyles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className={dialogStyles.header}>
          <h2 id={titleId} className={dialogStyles.title}>
            {t('equipment.requalDashboard.completeDialog.title', {
              defaultValue: 'Complete requalification',
            })}
          </h2>
          <p id={descId} className={dialogStyles.desc}>
            {t('equipment.requalDashboard.completeDialog.subtitle', {
              defaultValue:
                'Close the requalification for {{tag}} ({{name}}). Provide the signed REQUAL qualification document id and your signature bundle id.',
              tag: row.equipment_tag,
              name: row.equipment_name,
            })}
          </p>
        </header>

        <form className={dialogStyles.form} onSubmit={onSubmit} noValidate>
          {error && (
            <div role="alert" className={dialogStyles.errorBanner}>
              {error}
            </div>
          )}

          <div className={dialogStyles.field}>
            <label htmlFor="rq-comp-doc" className={dialogStyles.label}>
              {t('equipment.requalDashboard.completeDialog.doc', {
                defaultValue: 'Qualification document id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="rq-comp-doc"
              ref={firstRef}
              className={dialogStyles.input}
              value={docId}
              onChange={(e) => setDocId(e.target.value)}
              placeholder="doc-…"
              required
            />
            <p className={styles.hint}>
              {t('equipment.requalDashboard.completeDialog.docHint', {
                defaultValue:
                  'Must be a signed REQUAL-kind document — the server verifies the kind and signature state.',
              })}
            </p>
          </div>

          <div className={dialogStyles.field}>
            <label htmlFor="rq-comp-sig" className={dialogStyles.label}>
              {t('equipment.requalDashboard.completeDialog.sig', {
                defaultValue: 'Signature bundle id',
              })}
              <span className={dialogStyles.required} aria-hidden="true">*</span>
            </label>
            <input
              id="rq-comp-sig"
              className={dialogStyles.input}
              value={sig}
              onChange={(e) => setSig(e.target.value)}
              placeholder="sig-…"
              required
            />
          </div>

          <footer className={dialogStyles.footer}>
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={completeM.isPending}
            >
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              type="submit"
              variant="primary"
              isLoading={completeM.isPending}
            >
              {t('equipment.requalDashboard.completeDialog.submit', {
                defaultValue: 'Complete',
              })}
            </Button>
          </footer>
        </form>
      </div>
    </div>
  );
}
